import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from 'components/Toast/useToast';
import client from 'utils/client';
import { useAuth } from 'contexts/AuthContext';
import { PasswordInput } from 'components/FormElements/TextInput';
import './login.css';

const MIN_PASSWORD_LENGTH = 8;
// bcrypt ignores everything past 72 bytes, and the API rejects it. Bytes, not
// characters: emoji and accented letters take several bytes each.
const MAX_PASSWORD_BYTES = 72;

const initialValues = { password: '', confirm: '' };

const byteLength = (value) => new TextEncoder().encode(value).length;

const validate = (values) => {
  const errors = {};
  if (values.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  } else if (byteLength(values.password) > MAX_PASSWORD_BYTES) {
    errors.password = `Password is too long (${MAX_PASSWORD_BYTES} bytes at most). Accented letters and emoji count for more than one byte each.`;
  }
  if (!values.confirm) {
    errors.confirm = 'Please confirm your password';
  } else if (values.confirm !== values.password) {
    errors.confirm = 'Passwords do not match';
  }
  return errors;
};

const GENERIC_ERROR = 'Something went wrong. Check your connection and try again.';

// A 410 is a dead link, whatever the reason — the API never says which. Anything else,
// including a transport failure, says nothing about the link and offers a retry.

function SetPassword({ setToken }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'loading' : 'dead');
  const [attempt, setAttempt] = useState(0);
  const [email, setEmail] = useState('');
  const { onOpen } = useToast();
  const { setUserDetails } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    client
      // POST so the token travels in the body: a URL path is written to request logs.
      .post('/authentication/portal-token', { token })
      .then(({ data }) => {
        if (cancelled) return;
        setEmail(data.data.email);
        setStatus('ready');
      })
      .catch((err) => {
        if (!cancelled) setStatus(err?.status === 410 ? 'dead' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const retry = () => {
    setStatus('loading');
    setAttempt((n) => n + 1);
  };

  const handleSubmit = (values) =>
    client
      .post('/authentication/set-password', { token, password: values.password })
      .then(({ data }) => {
        const { token: _session, ...rest } = data.data;
        setUserDetails({ ...rest });
        setToken(data.data);
        // setToken swaps App to the signed-in routes, which have no /set-password, so
        // leave this URL in the same batch. replace keeps the spent link out of history.
        navigate('/', { replace: true });
      })
      .catch((err) => {
        if (err?.status === 410) {
          setStatus('dead');
          return;
        }
        onOpen({ type: 'error', message: err?.status === 400 ? err.message : GENERIC_ERROR });
      });

  return (
    <div className='auth-shell'>
      <div className='auth-card'>
        <div className='auth-brand'>
          <span className='auth-brand-mark' aria-hidden='true'>GTI</span>
          <span className='auth-brand-word'>Microfinance</span>
        </div>

        {status === 'loading' && <p className='auth-subtitle' role='status'>Checking your link…</p>}

        {status === 'dead' && (
          <>
            <h1 className='auth-title'>This link has expired or has already been used</h1>
            <p className='auth-subtitle'>Request a new link and we will send it to your email address.</p>
            <Link className='btn-primary auth-submit auth-button-link' to='/request-link'>Request a new link</Link>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className='auth-title'>Something went wrong</h1>
            <p className='auth-subtitle'>We could not check your link. Check your connection and try again.</p>
            <button type='button' className='btn-primary auth-submit' onClick={retry}>Try again</button>
          </>
        )}

        {status === 'ready' && (
          <>
            <h1 className='auth-title'>Set your password</h1>
            <p className='auth-subtitle'>Choose a password to finish setting up your account.</p>

            <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
              {({ isSubmitting }) => (
                <Form className='auth-form'>
                  <div className='form-field'>
                    <label htmlFor='set-password-email'>Email</label>
                    <input id='set-password-email' type='email' value={email} readOnly autoComplete='username' />
                  </div>
                  <div>
                    <PasswordInput className='form-field' name='password' label='New password' placeholder='Enter a new password' autoComplete='new-password' autoFocus required />
                    <p className='auth-hint'>At least {MIN_PASSWORD_LENGTH} characters</p>
                  </div>
                  <PasswordInput className='form-field' name='confirm' label='Confirm password' placeholder='Re-enter your password' autoComplete='new-password' required />
                  <button type='submit' className='btn-primary auth-submit' disabled={isSubmitting}>
                    {isSubmitting ? 'Saving…' : 'Set password'}
                  </button>
                </Form>
              )}
            </Formik>
          </>
        )}

        {status !== 'ready' && (
          <p className='auth-footer'>
            <Link className='auth-link' to='/'>Back to sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default SetPassword;
